// src/pages/SubscriptionSuccess.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Container,
  Paper,
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

// Get correct API base URL (same logic as api/client.js)
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    // Use proxy approach for better security - Vercel forwards /api/* to backend
    return '/api';
  }
};

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your subscription...');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10; // Try for 30 seconds (10 attempts * 3 seconds)

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    // Try to get clinic ID from URL first, then fall back to localStorage
    const clinicIdFromUrl = searchParams.get('clinic_id');
    const clinicIdFromStorage = localStorage.getItem('expired_clinic_id');
    const clinicId = clinicIdFromUrl || clinicIdFromStorage;
    
    console.log('🎯 Subscription Success Page Loaded');
    console.log('Session ID:', sessionId);
    console.log('Clinic ID from URL:', clinicIdFromUrl);
    console.log('Clinic ID from storage:', clinicIdFromStorage);
    console.log('Using Clinic ID:', clinicId);

    // If we don't have a clinic ID, we can't proceed
    if (!clinicId) {
      console.error('❌ No clinic ID available - cannot verify subscription');
      setStatus('error');
      setMessage('Unable to verify your subscription. Please try logging in manually.');
      return;
    }

    let currentAttempt = 0;
    let pollInterval;

    // Poll for subscription status update
    const checkSubscriptionStatus = async () => {
      try {
        currentAttempt++;
        console.log(`🔄 Polling attempt ${currentAttempt}/${maxAttempts}`);
        setAttempts(currentAttempt);
        
        // First, try to get a new token using the clinic ID
        console.log('🚀 Making request with clinic ID:', clinicId);
        
        const response = await fetch(`${getApiBaseUrl()}/billing/post-payment-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Clinic-ID': clinicId
          },
          body: JSON.stringify({
            session_id: sessionId
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.subscription_active) {
            console.log('✅ Subscription is now active!');
            
            // Store the new token if provided
            if (data.access_token) {
              console.log('🔐 Received new JWT token with updated subscription');
              localStorage.setItem('authToken', data.access_token);
              localStorage.setItem('auth_token', data.access_token);
              
              // Clear the expired clinic ID since we're no longer expired
              localStorage.removeItem('expired_clinic_id');
              localStorage.removeItem('pre_expiry_url');
            }
            
            setStatus('success');
            setMessage('Your subscription has been successfully reactivated! Redirecting to dashboard...');
            
            // Clear the interval and redirect
            clearInterval(pollInterval);
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 2000);
            
            return;
          } else {
            console.log('⏳ Subscription not yet active, webhook may still be processing...');
          }
        } else if (response.status === 404) {
          console.log('⏳ Subscription not found yet, continuing to poll...');
        } else {
          throw new Error(`Unexpected response: ${response.status}`);
        }
      } catch (error) {
        console.error('❌ Error checking subscription status:', error);
      }
      
      // Check if we've exceeded max attempts
      if (currentAttempt >= maxAttempts) {
        console.log('⏰ Max polling attempts reached, showing error state');
        clearInterval(pollInterval);
        setStatus('error');
        setMessage('There was an issue verifying your subscription. Please try logging in or contact support.');
        return;
      }
      
      // Schedule next check
      pollInterval = setTimeout(checkSubscriptionStatus, 3000);
    };

    // Start polling immediately
    checkSubscriptionStatus();

    // Cleanup
    return () => {
      if (pollInterval) {
        clearTimeout(pollInterval);
      }
    };
  }, [searchParams, maxAttempts]);

  const handleManualLogin = () => {
    // Clear any stale tokens and redirect to login
    localStorage.removeItem('authToken');
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@medinsight.pro?subject=Subscription%20Activation%20Issue';
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {status === 'processing' && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please wait while we activate your subscription...
              <br />
              (Attempt {attempts}/{maxAttempts})
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Subscription Activated!
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {message}
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 3 }} />
            <Typography variant="h5" gutterBottom color="error">
              Activation Delayed
            </Typography>
            <Alert severity="warning" sx={{ mt: 2, mb: 3 }}>
              {message}
            </Alert>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Your payment was successful, but there's a slight delay in activating your subscription.
              This usually resolves within a few minutes.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={handleManualLogin}
              >
                Try Logging In
              </Button>
              <Button
                variant="outlined"
                onClick={handleContactSupport}
              >
                Contact Support
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SubscriptionSuccess;